import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, inject, Injectable, isDevMode, NgModule } from '@angular/core';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthClientConfig } from '@auth0/auth0-angular';
import { BulkUploaderModule } from '@frontend/common/bulk-uploader';
import { CorrelationIdModule } from '@frontend/common/correlation-id';
import { SharedUiDowntimeNotificationModule } from '@frontend/common/downtime-notification';
import { SharedIdleModule } from '@frontend/common/idle';
import { InterceptorModule } from '@frontend/common/interceptor';
import { LoadingModule } from '@frontend/common/loading';
import {
  PhAuthModule,
  PhAuthServiceConfig,
  PH_AUTH_SERVICE_CONFIG,
  provideBootstrapEffects,
  PhAuthService
} from '@frontend/common/ph-auth';
import {
  PhConfigLoaderModel,
  PhConfigLoaderModule,
  PhConfigLoaderService,
} from '@frontend/common/ph-config-loader';
import {
  CustomSerializer,
  RouterEffects,
  routerReducerInitialState,
  RouterStateUrl,
} from '@frontend/common/ph-router-store';
import { UpdaterConfig, UpdaterModule } from '@frontend/common/updater';
import {
  CurrentUserService,
  CustomMatPaginatorIntl,
  SecurableModule,
  setupSentry,
  UserNotificationService,
} from '@frontend/common/util';
import { CustomersModule } from '@frontend/entity';
import { StaffApiEffects, StaffWsEffects } from '@frontend/shared/staff/data-access/src';
import {
  WorkflowApiEffects,
  WorkflowWsEffects,
} from '@frontend/shared/workflow/data-access/src';
import { DepartmentApiEffects, DepartmentWsEffects } from '@frontend/shared/department/data-access/src';
import { KioskApiEffects, KioskWsEffects } from '@frontend/shared/kiosk/data-access/src';
import { UiAccountDisableModule } from '@frontend/ui/account-disable/src';
import { PatientRecipientsModule } from '@frontend/ui/patient-recipients';
import { WorkflowRtFeatureVisitDetailModule } from '@frontend/workflow-rt/feature/visit-detail/src';
import { EffectsModule } from '@ngrx/effects';
import * as fromRouter from '@ngrx/router-store';
import {
  FullRouterStateSerializer,
  RouterStateSerializer,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { ActionReducerMap, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { provideNamedApollo } from 'apollo-angular';
import { HttpLink } from '@apollo/client';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AppContentWrapperModule } from './app-content-wrapper/app-content-wrapper.module';
import { DepartmentSelectorModule } from './app-content-wrapper/department-selector/department-selector.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppEffects } from './app.effects';
import { AppGuard } from './app.guard';
import { GraphQlGuard } from './graphql.guard';
import * as fromApp from './app.reducer';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';

interface State {
  routerReducer: fromRouter.RouterReducerState<RouterStateUrl>;
  app: fromApp.State;
}

export const reducers: ActionReducerMap<State> = {
  routerReducer: fromRouter.routerReducer,
  app: fromApp.reducer,
};

@Injectable()
export class PhConfigLoaderFactory {
  constructor(private http: HttpClient) { }
  cb = (config: PhConfigLoaderModel): Observable<PhConfigLoaderModel> =>
    this.http.get<{ id: number; auth_org_id: string; locale: string }>(config.APP_API_URL + 'customers/id').pipe(
      map((customer) => customer),
      catchError(() => of({ id: 'disabled', auth_org_id: null, locale: null })),
      map((customer) => ({
        ...config,
        CUSTOMER_ID: customer.id,
        AUTH_ORG_ID: customer.auth_org_id,
        LOCALE: customer.locale,
      })),
      tap(({ SENTRY_DSN }: any) => {
        if (!isDevMode()) {
          setupSentry(
            SENTRY_DSN,
            window.location.href,
            environment.version,
            'workflow-rt'
          );
        }
      })
    );
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    MatBottomSheetModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatListModule,
    MatSidenavModule,
    MatToolbarModule,
    StoreModule.forRoot(reducers, {
      metaReducers: [],
      initialState: { routerReducer: routerReducerInitialState },
      runtimeChecks: { strictStateImmutability: true, strictActionImmutability: true },
    }),
    EffectsModule.forRoot([RouterEffects, AppEffects]),
    StoreRouterConnectingModule.forRoot({ serializer: FullRouterStateSerializer }),
    !environment.production ? StoreDevtoolsModule.instrument({ maxAge: 100, logOnly: !isDevMode(), connectInZone: true }) : [],
    AppRoutingModule,
    UpdaterModule.forRoot(
      [PhConfigLoaderService],
      (appConfig: PhConfigLoaderService): UpdaterConfig => ({
        checkInterval: appConfig.getConfig('CHECK_RELEASE_AFTER_SEC'),
      })
    ),
    PhAuthModule,
    PhConfigLoaderModule.forRoot(PhConfigLoaderFactory),
    BulkUploaderModule.forRoot(),
    CorrelationIdModule,
    CustomersModule,
    UiAccountDisableModule,
    SharedIdleModule,
    PatientRecipientsModule,
    InterceptorModule,
    SharedUiDowntimeNotificationModule,
    SecurableModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    LoadingModule,
    AppContentWrapperModule,
    DepartmentSelectorModule,
    WorkflowRtFeatureVisitDetailModule,
  ],
  providers: [
    AppGuard,
    GraphQlGuard,
    provideNamedApollo(() => {
      const phAuthSrv: PhAuthService = inject(PhAuthService);
      const config: PhConfigLoaderService = inject(PhConfigLoaderService);
      const authContext = setContext(() => {
        const token = phAuthSrv.token;
        return {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      });

      const hasuraUrl = config.getConfig('GRAPHQL_URL');
      const caGraphqlUrl = config.getConfig('CA_GRAPHQL_URL');
      const apiGatewayUrl = config.getConfig('CONNECTRT_PRT_API_GATEWAY_URL');

      const hasuraWsUrl = hasuraUrl.startsWith('https')
        ? hasuraUrl.replace('https://', 'wss://')
        : hasuraUrl.replace('http://', 'ws://');

      const hasuraWsClient = createClient({
        url: hasuraWsUrl,
        connectionParams: () => {
          const token = phAuthSrv.token;
          return {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
        },
      });

      const apiGatewayWsClient = createClient({
        url: apiGatewayUrl,
        connectionParams: () => {
          const token = phAuthSrv.token;
          return {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
        },
      });

      const hasuraWsLink = new GraphQLWsLink(hasuraWsClient);

      const caHttp = new HttpLink({
        uri: caGraphqlUrl,
      })
      const caLink = ApolloLink.from([authContext, caHttp]);

      const apiGatewayHttp = new HttpLink({
        uri: apiGatewayUrl,
      });
      const apiGatewayLink = ApolloLink.from([authContext, apiGatewayHttp]);
      const apiGatewayWsLink = new GraphQLWsLink(apiGatewayWsClient);

      return {
        default: {
          link: hasuraWsLink,
          cache: new InMemoryCache(),
        },
        'HASURA': {
          link: hasuraWsLink,
          cache: new InMemoryCache(),
        },
        'API_GATEWAY_WS': {
          link: apiGatewayWsLink,
          cache: new InMemoryCache(),
        },
        'CA': {
          link: caLink,
          cache: new InMemoryCache(),
        },
        'API_GATEWAY': {
          link: apiGatewayLink,
          cache: new InMemoryCache(),
        },
      };
    }),
    CurrentUserService,
    UserNotificationService,
    provideBootstrapEffects([
      WorkflowApiEffects,
      WorkflowWsEffects,
      DepartmentApiEffects,
      DepartmentWsEffects,
      WorkflowApiEffects,
      WorkflowWsEffects,
      KioskApiEffects,
      KioskWsEffects,
      StaffApiEffects,
      StaffWsEffects,
    ]),
    {
      provide: APP_INITIALIZER,
      deps: [PhConfigLoaderService, AuthClientConfig],
      useFactory: (ac: PhConfigLoaderService) => () => ac.load(),
      multi: true,
    },
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    {
      provide: ErrorHandler,
      useFactory: () => {
        if (environment.production) {
          return Sentry.createErrorHandler();
        }
        return new ErrorHandler();
      },
    },
    {
      provide: PH_AUTH_SERVICE_CONFIG,
      useFactory: phAuthServiceFactory,
      deps: [PhConfigLoaderService],
    },
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }

function phAuthServiceFactory(configSrv: PhConfigLoaderService): PhAuthServiceConfig {
  return combineLatest({
    APP_API_URL: configSrv.getConfig$('APP_API_URL'),
    AUTH_DOMAIN: configSrv.getConfig$('AUTH_DOMAIN'),
    AUTH_AUDIENCE: configSrv.getConfig$('AUTH_AUDIENCE'),
    REFRESH_TOKEN_IN_ADVANCE_SEC: configSrv.getConfig$('REFRESH_TOKEN_IN_ADVANCE_SEC'),
    AUTH_LOGOUT_CALLBACK_URL: configSrv.getConfig$('AUTH_LOGOUT_CALLBACK_URL'),
    AUTH_ORG_ID: configSrv.getConfig$('AUTH_ORG_ID'),
    LOCALE: configSrv.getConfig$('LOCALE'),
    CA_API_URL: configSrv.getConfig$('CA_API_URL'),
  }).pipe(
    map(({
      APP_API_URL,
      AUTH_DOMAIN,
      AUTH_AUDIENCE,
      REFRESH_TOKEN_IN_ADVANCE_SEC,
      AUTH_LOGOUT_CALLBACK_URL,
      AUTH_ORG_ID,
      LOCALE,
      CA_API_URL,
    }) => ({
      APP_API_URL,
      AUTH_DOMAIN,
      AUTH_AUDIENCE,
      REFRESH_TOKEN_IN_ADVANCE_SEC,
      AUTH_LOGOUT_CALLBACK_URL,
      AUTH_ORG_ID,
      LOCALE,
      CA_API_URL,
    }))
  );
}
